import { Injectable, inject } from '@angular/core';
import { map, Observable, take, first, switchMap, filter } from 'rxjs';
import {
    ConsumptionProfile,
    EnergySource,
    ProjectType,
    RoofType,
    HeatGeneratorType,
    GEGSystemType,
    HeatloadHeaders,
    BuildingPlacement
} from '@nx-customer-apps/shared/enums';
import { BuildingType, HeatLoadCalculationMethod, LodashService } from '@nx-customer-apps/components';
import {
    AppState,
    HeatloadState,
    ModernizationsAndInsulation,
    RoofDimensions,
    SuccessMessageOptions
} from '@nx-customer-apps/shared/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { FullPartial, windowPercentageAreaDictionary, centimetersToMeters, roundTo } from '@nx-customer-apps/shared/utils';
import {
    BuildingGetById,
    BuildingPartialPatchV1Patch,
    BuildingsControllerService,
    CalculationsControllerService,
    DevicesControllerService,
    HeatGeneratorEnergySourceInformation,
    EnergyStandard,
    HeatGeneratorInformation,
    HeatLoadByBuildingHullRequestPost,
    HeatLoadByBuildingHullResponse,
    HeatLoadByConsumptionRequestPost,
    HeatLoadByConsumptionResponse,
    HeatLoadByCustomValueRequestPost,
    HeatLoadByCustomValueResponse,
    HeatLoadByEnergyCertRequestPost,
    HeatLoadByEnergyCertResponse,
    HeatLoadByLivingAreaRequestPost,
    HeatLoadByLivingAreaResponse,
    HeatLoadCalculationGetById,
    ProjectResponseGetById,
    Roof,
    RoofPartialPatchV1Patch,
    SystemItem,
    SystemsRequestPost,
    UpdateProjectRequestPartialPatchV1Patch,
    WindowInformation,
    Installation,
    HeatLoadByInstallationResponse,
    HeatLoadByInstallationRequestPost
} from '@nx-customer-apps/api-planning-projects';
import { ProjectStore } from '../../state/project/project.store';
import { GlobalStore } from '../../state/global/global.store';
import { DEFAULT_CONSUMPTION_PROFILE, DEFAULT_EXHAUST_GAS_LOSSES } from '../../shared/utils/constants';
import { AppSpecificDataStore } from '../../state/app-specific-data/app-specific-data.store';

@Injectable({
    providedIn: 'root'
})
export class HeatloadService {
    private translate = inject(TranslateService);
    private calculationsControllerService = inject(CalculationsControllerService);
    private devicesControllerService = inject(DevicesControllerService);
    private buildingsControllerService = inject(BuildingsControllerService);
    private projectStore = inject(ProjectStore);
    private globalStore = inject(GlobalStore);
    private appSpecifiCDataStore = inject(AppSpecificDataStore);

    public isNewConstruction(projectType: ProjectType): boolean {
        return projectType === ProjectType.NewConstruction;
    }

    public getHeatGenerators(gegSystemType?: GEGSystemType): Observable<HeatGeneratorInformation[]> {
        return this.projectStore.project$.pipe(
            first(),
            switchMap(project => {
                const countryCode = project!.address.countryCode;
                return this.devicesControllerService
                    .devicesControllerGetHeatGenerators({ countryCode: countryCode as 'DE' | 'BE' | 'AT', gegSystemType })
                    .pipe(map(response => response.heatGenerators));
            })
        ) as Observable<HeatGeneratorInformation[]>;
    }

    public getConnectedDevices(search?: string): Observable<Installation[]> {
        return this.projectStore.project$.pipe(
            first(),
            switchMap(project => {
                const request: { id: string; search?: string } = { id: project!.id!, search: search || '' };
                return this.devicesControllerService
                    .devicesControllerGetInstallations(request)
                    .pipe(map(response => response.installations));
            })
        ) as Observable<Installation[]>;
    }

    public getHeatingSystems(
        energySource: EnergySource,
        installationConstructionYear: number,
        heatGeneratorType: HeatGeneratorType,
        gegSystemType?: GEGSystemType
    ): Observable<SystemItem[]> {
        return this.projectStore.project$.pipe(
            filter(Boolean),
            first(),
            switchMap(project => {
                const countryCode = project!.address.countryCode;
                const request: SystemsRequestPost = {
                    countryCode,
                    energySource,
                    installationConstructionYear,
                    heatGeneratorType,
                    gegSystemType
                } as SystemsRequestPost;
                return this.devicesControllerService
                    .devicesControllerGetSystems({ body: request as SystemsRequestPost })
                    .pipe(map(response => response.systems));
            })
        );
    }

    public getConsumptionProfiles(projectType: ProjectType, buildingType: BuildingType): Observable<ConsumptionProfile[]> {
        return this.projectStore.project$.pipe(
            first(),
            switchMap(project => {
                const countryCode = project!.address.countryCode;
                const request = { countryCode, projectType, buildingType };
                return this.buildingsControllerService
                    .buildingsControllerConsumptionProfiles(
                        request as {
                            buildingType: 'ONE_FAMILY_HOUSE' | 'MULTI_FAMILY_HOUSE';
                            countryCode: 'DE' | 'BE' | 'AT';
                            projectType: 'MODERNISATION' | 'NEW_BUILDING';
                        }
                    )
                    .pipe(map(response => response.profiles));
            })
        ) as Observable<ConsumptionProfile[]>;
    }

    public getWindows(
        windowsConstructionYearProvidedByUser: string | undefined,
        buildingConstructionYear: string | undefined
    ): Observable<WindowInformation[]> {
        return this.projectStore.project$.pipe(
            first(),
            switchMap(project => {
                const countryCode = project!.address.countryCode;
                const projectCreateDate = new Date(project!.createdDate!).getFullYear();
                const windowsConstructionYear = windowsConstructionYearProvidedByUser
                    ? +windowsConstructionYearProvidedByUser
                    : buildingConstructionYear
                      ? +buildingConstructionYear
                      : projectCreateDate;

                const request = {
                    countryCode,
                    windowsConstructionYear
                };
                return this.buildingsControllerService
                    .buildingsControllerWindows(
                        request as {
                            countryCode: 'DE' | 'BE' | 'AT';
                            windowsConstructionYear: number;
                        }
                    )
                    .pipe(map(response => response.windowTypes));
            })
        ) as Observable<WindowInformation[]>;
    }

    public getEnergyStandards(): Observable<EnergyStandard[]> {
        return this.projectStore.project$.pipe(
            first(),
            switchMap(project => {
                const countryCode = project!.address.countryCode;
                const request = { countryCode };
                return this.buildingsControllerService
                    .buildingsControllerEnergyStandards(request)
                    .pipe(map(response => response.energyStandards));
            })
        );
    }

    public calculateByConsumption(): Observable<HeatLoadByConsumptionResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload: { heatGeneratorDetails, energyConsumption, heatingEnergySource, heatingDistribution, solarThermalHeating },
                    project: { selectedProject }
                } = state;

                let request = {
                    buildingType: selectedProject!.building.buildingType,
                    energySourceConsumption: energyConsumption?.requiredEnergyPerYear,
                    electricityConsumption: energyConsumption?.nonHeatingElectricityPerYear,
                    exhaustGasLosses: heatGeneratorDetails?.gasLossExhaust || DEFAULT_EXHAUST_GAS_LOSSES,
                    heatingSystemType: heatGeneratorDetails?.boilerType,
                    lowTemperatureOption: !!heatGeneratorDetails?.lowTemperature,
                    warmWaterByHeatGenerator: !!heatGeneratorDetails?.warmWaterByHeatGenerator,
                    heatGeneratorPower: heatGeneratorDetails?.nominalPower,
                    heatPumpOperatingMode: heatGeneratorDetails?.heatPumpOperatingMode,
                    installationConstructionYear: heatGeneratorDetails ? +heatGeneratorDetails.installationYear! : undefined,
                    consumptionProfileForHeating: heatingDistribution?.heatingSchedule,
                    solarThermalHeatingType: solarThermalHeating?.type,
                    numberOfPersons: selectedProject!.building?.numberOfPersons,
                    postalCode: selectedProject!.address!.postalCode,
                    countryCode: selectedProject!.address!.countryCode,
                    energyPrices: selectedProject!.energyPrices,
                    energySource: heatingEnergySource?.selectedEnergySource.type
                } as HeatLoadByConsumptionRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByConsumptionRequestPost>(request, selectedProject);

                return request;
            }),
            take(1),
            switchMap(request =>
                this.calculationsControllerService.calculationsControllerConsumption({
                    body: request as HeatLoadByConsumptionRequestPost
                })
            )
        ) as Observable<HeatLoadByConsumptionResponse>;
    }

    public calculateByInstallation(): Observable<HeatLoadByInstallationResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload: { deviceSelection },
                    project: { selectedProject }
                } = state;

                let request = {
                    buildingType: selectedProject?.building.buildingType,
                    installationId: deviceSelection?.selectedInstallationId,
                    numberOfPersons: selectedProject?.building.numberOfPersons
                } as HeatLoadByInstallationRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByInstallationRequestPost>(request, selectedProject);
                return request;
            }),
            take(1),
            switchMap(request =>
                this.calculationsControllerService
                    .calculationsControllerInstallation({
                        body: request as HeatLoadByInstallationRequestPost
                    })
                    .pipe(
                        map(heatload => ({
                            ...heatload,
                            outsideTemperature: roundTo(heatload.outsideTemperature, 1),
                            flowTemperature: roundTo(heatload.flowTemperature),
                            baseHeatLoad: roundTo(heatload.baseHeatLoad, 1),
                            lockTimeHeatLoad: roundTo(heatload.lockTimeHeatLoad, 1),
                            warmWaterHeatLoad: roundTo(heatload.warmWaterHeatLoad, 1)
                        }))
                    )
            )
        ) as Observable<HeatLoadByInstallationResponse>;
    }

    public calculateByLivingArea(): Observable<HeatLoadByLivingAreaResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload,
                    project: { selectedProject }
                } = state;
                let request = {
                    buildingType: selectedProject!.building.buildingType,
                    projectType: selectedProject!.projectType,
                    livingArea: heatload.livingArea!.value,
                    numberOfPersons: selectedProject!.building.numberOfPersons,
                    countryCode: selectedProject!.address!.countryCode,
                    postalCode: selectedProject!.address.postalCode,
                    projectCreationYear: new Date(selectedProject?.createdDate!).getFullYear()
                } as HeatLoadByLivingAreaRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByLivingAreaRequestPost>(request, selectedProject);
                request = this.addOptionalBuildingPlacementProperty(request, selectedProject);

                if (selectedProject?.projectType === ProjectType.NewConstruction) {
                    request.energyStandard = heatload.selectedEnergyStandard?.key;
                    return request;
                }

                const buildingConstructionYear = +heatload.modernization!.buildingConstructionYear;
                request.buildingConstructionYear = buildingConstructionYear;
                request.wallsModernizationYear = +(heatload.modernization!.wallsModernization || buildingConstructionYear);
                request.windowsModernizationYear = +(heatload.modernization!.windowsModernization || buildingConstructionYear);
                request.roofModernizationYear = +(heatload.modernization!.roofModernization || buildingConstructionYear);
                request.ventilationHeatRecovery = heatload.ventilation!.value;
                return request;
            }),
            take(1),
            switchMap(request =>
                this.calculationsControllerService.calculationsControllerLivingArea({
                    body: request as HeatLoadByLivingAreaRequestPost
                })
            )
        ) as Observable<HeatLoadByLivingAreaResponse>;
    }

    public calculateByEnergyCertificate(): Observable<HeatLoadByEnergyCertResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload,
                    project: { selectedProject }
                } = state;
                let request = {
                    buildingType: selectedProject!.building.buildingType,
                    countryCode: selectedProject!.address!.countryCode,
                    postalCode: selectedProject!.address.postalCode,
                    numberOfPersons: selectedProject!.building.numberOfPersons,
                    transmissionHeatLoss: heatload.energyCertificate!.specificTransmissionHeatLoss,
                    buildingVolume: heatload.energyCertificate!.buildingVolume,
                    buildingEnclosureArea: heatload.energyCertificate!.surroundingArea,
                    airExchangeRate: heatload.energyCertificate!.airExchangeRate
                } as HeatLoadByEnergyCertRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByEnergyCertRequestPost>(request, selectedProject);

                return request;
            }),
            take(1),
            switchMap(request =>
                this.calculationsControllerService.calculationsControllerEnergyCertificate({
                    body: request as HeatLoadByEnergyCertRequestPost
                })
            )
        ) as Observable<HeatLoadByEnergyCertResponse>;
    }

    public calculateByBuildingHull(): Observable<HeatLoadByBuildingHullResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload,
                    project: { selectedProject }
                } = state;

                const projectCreationYear = new Date(selectedProject?.createdDate!).getFullYear();
                let request = {
                    countryCode: selectedProject!.address!.countryCode,
                    postalCode: selectedProject!.address.postalCode,
                    numberOfPersons: selectedProject!.building.numberOfPersons,
                    projectCreationYear,
                    buildingType: selectedProject!.building.buildingType,
                    buildingPlacement: heatload.buildingHull!.buildingPosition,
                    buildingLength: heatload.buildingHull!.buildingDimensions.buildingLength,
                    buildingWidth: heatload.buildingHull!.buildingDimensions.buildingWidth,
                    buildingLevelHeight: heatload.buildingHull!.buildingDimensions.floorHeight,
                    buildingLevelsCount: heatload.buildingHull!.buildingDimensions.numberOfFloors,
                    ventilationHeatRecovery: !!heatload.buildingHull!.ventilationAndHeating.ventilationPresent,
                    hasCellar: !!heatload.buildingHull!.ventilationAndHeating.basementPresent,
                    heatedCellar: !!heatload.buildingHull!.ventilationAndHeating.basementHeated,
                    hasAttic: !!heatload.buildingHull!.ventilationAndHeating.atticPresent,
                    heatedAttic: !!heatload.buildingHull!.ventilationAndHeating.atticHeated,
                    windowsArea: windowPercentageAreaDictionary[heatload.windowsAndGlazing!.windowsPercentageArea],
                    windowsType: heatload.windowsAndGlazing!.typeOfGlazing,
                    roof: this.roofToBuildingHullCalculation(heatload)
                } as HeatLoadByBuildingHullRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByBuildingHullRequestPost>(request, selectedProject);
                request = this.addConsumptionProfileForHeatingProperty(request, selectedProject);

                if (selectedProject?.projectType === ProjectType.Renovation) {
                    const modernizations = this.stateToBuildingHullModernization(heatload.modernizationsAndInsulation!);
                    const requestWithModernization = {
                        ...request,
                        ...modernizations
                    };
                    return requestWithModernization as HeatLoadByBuildingHullRequestPost;
                }
                const insulations = this.stateToInsulation(heatload.modernizationsAndInsulation!);
                const requestWithoutModernization = {
                    ...request,
                    ...insulations
                };
                return requestWithoutModernization as HeatLoadByBuildingHullRequestPost;
            }),
            take(1),
            switchMap(request =>
                this.calculationsControllerService.calculationsControllerBuildingHull({
                    body: request as HeatLoadByBuildingHullRequestPost
                })
            )
        ) as Observable<HeatLoadByBuildingHullResponse>;
    }

    public calculateByCustomValue(): Observable<HeatLoadByCustomValueResponse> {
        return this.globalStore.state$.pipe(
            map(state => {
                const {
                    heatload,
                    project: { selectedProject }
                } = state;
                let request = {
                    buildingType: selectedProject!.building.buildingType,
                    customBaseHeatLoad: +heatload.definedValue!,
                    numberOfPersons: selectedProject!.building.numberOfPersons
                } as HeatLoadByCustomValueRequestPost;

                request = this.addOptionalDailyLockPeriodProperty<HeatLoadByCustomValueRequestPost>(request, selectedProject);

                return request;
            }),
            take(1),
            switchMap(request => this.calculationsControllerService.calculationsControllerCustomValue({ body: request }))
        ) as Observable<HeatLoadByCustomValueResponse>;
    }

    /**
     * Builds request payload for patching project with calculated heatload and other information gathered in heatload state.
     */
    public stateToSaveHeatload(
        state: AppState,
        buildingType: BuildingType,
        heatloadResult: Partial<HeatLoadCalculationGetById> | null,
        includeCalculations?: boolean
    ): UpdateProjectRequestPartialPatchV1Patch {
        const { heatload } = state;
        const calculationMethod = this.getCalculationMethod(heatload);
        const heatLoadCalculation = this.buildHeatloadCalculation(calculationMethod!, heatloadResult, buildingType);
        const heatLoadCalculations = [heatLoadCalculation];
        const options = includeCalculations ? { state, heatLoadCalculations } : { state };

        if (heatloadResult === null) {
            throw new Error('Heatload result is required for saving heatload calculation');
        }

        if (calculationMethod === HeatLoadCalculationMethod.Consumption) {
            return this.stateToHeatloadByConsumption(options);
        }
        if (calculationMethod === HeatLoadCalculationMethod.LivingSpace) {
            return this.stateToHeatloadByLivingArea(options);
        }
        if (calculationMethod === HeatLoadCalculationMethod.EnergyCertificate) {
            return this.stateToHeatloadByEnergyCertificate(options);
        }
        if (calculationMethod === HeatLoadCalculationMethod.BuildingHull) {
            return this.stateToHeatloadByBuildingHull(options);
        }
        if (calculationMethod === HeatLoadCalculationMethod.Installation) {
            return this.stateToHeatloadWithJustCalculation(options);
        }
        if (calculationMethod === HeatLoadCalculationMethod.CustomValue) {
            const definedValueOptions = includeCalculations ? { heatloadResult, heatLoadCalculations } : { heatloadResult };
            return this.stateToHeatloadByDefinedValue(definedValueOptions);
        }
        return {};
    }

    /**
     * Builds a project patch containing a room-by-room heatload calculation before the user edits
     * the room plan on the dedicated room-by-room screen.
     */
    public buildRoomByRoomHeatloadCalculation(building: BuildingGetById): UpdateProjectRequestPartialPatchV1Patch {
        const heatLoadCalculation = this.buildHeatloadCalculation(
            HeatLoadCalculationMethod.RoomByRoom,
            building.heatLoadCalculations?.[0] ?? null,
            building.buildingType as BuildingType
        );
        return { building: { heatLoadCalculations: [heatLoadCalculation] } };
    }

    /**
     * Builds request payload for patching project with manually provided result by user.
     */
    public stateToHeatloadByDefinedValue(options: {
        heatloadResult: Partial<HeatLoadCalculationGetById>;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const building: Partial<BuildingGetById> = { customBaseHeatLoad: options.heatloadResult?.baseHeatLoad };
        if (options.heatLoadCalculations) {
            building.heatLoadCalculations = options.heatLoadCalculations;
        }
        return { building };
    }

    public getInsulationTitle(projectType: ProjectType): string {
        return projectType === ProjectType.NewConstruction ? 'HEATLOAD.INSULATION.TITLE' : 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.TITLE';
    }

    public getModernizationsAndInsulationDescription(projectType: ProjectType): string | null {
        return projectType === ProjectType.Renovation ? 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.DESCRIPTION' : null;
    }

    public getHeader(calculationMethod?: HeatLoadCalculationMethod): HeatloadHeaders {
        switch (calculationMethod) {
            case HeatLoadCalculationMethod.LivingSpace:
                return HeatloadHeaders.ByLivingArea;
            case HeatLoadCalculationMethod.BuildingHull:
                return HeatloadHeaders.ByBuildingHull;
            case HeatLoadCalculationMethod.Consumption:
                return HeatloadHeaders.ByConsumption;
            case HeatLoadCalculationMethod.EnergyCertificate:
                return HeatloadHeaders.ByCertificate;
            case HeatLoadCalculationMethod.CustomValue:
                return HeatloadHeaders.HeatingLoad;
        }

        return HeatloadHeaders.HeatingLoad;
    }

    public isRoomByRoomCalculationSelected(calculationMethod: { value: HeatLoadCalculationMethod } | undefined): boolean {
        return calculationMethod?.value === HeatLoadCalculationMethod.RoomByRoom;
    }

    /**
     * Builds a heatload calculation for patching project data.
     */
    public buildHeatloadCalculation(
        calculationMethod: HeatLoadCalculationMethod,
        heatloadResult: Partial<HeatLoadCalculationGetById> | null,
        buildingType: BuildingType
    ): HeatLoadCalculationGetById {
        const calculation: HeatLoadCalculationGetById = {
            method: calculationMethod,
            baseHeatLoad: heatloadResult?.baseHeatLoad || 0,
            lockTimeHeatLoad: heatloadResult?.lockTimeHeatLoad || 0,
            orderDate: new Date().toISOString()
        } as HeatLoadCalculationGetById;

        if (buildingType === BuildingType.SingleFamilyHouse) {
            calculation.warmWaterHeatLoad = heatloadResult?.warmWaterHeatLoad || 0;
        }

        return calculation;
    }

    /**
     * Builds request payload for patching project with calculated heatload and specific information from energy certificate calculation method gathered in heatload state.
     */
    private stateToHeatloadByEnergyCertificate(options: {
        state: AppState;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const {
            heatload: { energyCertificate }
        } = options.state;

        const building: Partial<BuildingPartialPatchV1Patch> = {
            energyCertificate
        };
        if (options.heatLoadCalculations) {
            building.heatLoadCalculations = options.heatLoadCalculations;
        }
        return { building };
    }

    /**
     * Builds request payload for patching project with calculated heatload and specific information from installation calculation method gathered in heatload state.
     */
    private stateToHeatloadWithJustCalculation(options: {
        state: AppState;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const request: UpdateProjectRequestPartialPatchV1Patch = {};

        if (options.heatLoadCalculations) {
            request.building = { heatLoadCalculations: options.heatLoadCalculations };
        }

        return request;
    }

    /**
     * Builds request payload for patching project with calculated heatload and specific information from building hull calculation method gathered in heatload state.
     */
    private stateToHeatloadByBuildingHull(options: {
        state: AppState;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const {
            heatload: { buildingHull, roofType, roofDimensions, modernizationsAndInsulation, windowsAndGlazing },
            project: { selectedProject }
        } = options.state;

        const building: Partial<BuildingPartialPatchV1Patch> = {
            buildingPlacement: buildingHull!.buildingPosition,
            buildingLevelHeight: buildingHull!.buildingDimensions.floorHeight,
            buildingLevelsCount: buildingHull!.buildingDimensions.numberOfFloors,
            buildingLength: buildingHull!.buildingDimensions.buildingLength,
            buildingWidth: buildingHull!.buildingDimensions.buildingWidth,
            hasAttic: !!buildingHull?.ventilationAndHeating.atticPresent,
            heatedAttic: !!buildingHull?.ventilationAndHeating.atticHeated,
            hasCellar: !!buildingHull?.ventilationAndHeating.basementPresent,
            heatedCellar: !!buildingHull?.ventilationAndHeating.basementHeated,
            hasVentilation: !!buildingHull?.ventilationAndHeating.ventilationPresent,
            windowsType: windowsAndGlazing!.typeOfGlazing,
            windowsArea: windowPercentageAreaDictionary[windowsAndGlazing!.windowsPercentageArea],
            roof: this.stateToRoof(roofType?.value, roofDimensions)
        };

        if (options.heatLoadCalculations) {
            building.heatLoadCalculations = options.heatLoadCalculations;
        }

        if (!selectedProject?.building.consumptionProfileForHeating) {
            building.consumptionProfileForHeating = DEFAULT_CONSUMPTION_PROFILE;
        }

        if (selectedProject!.projectType === ProjectType.Renovation) {
            const modernizations = this.stateToBuildingHullModernization(modernizationsAndInsulation!);
            const buildingWithModernizations = {
                ...building,
                ...modernizations
            };

            return { building: buildingWithModernizations };
        }

        const insulations = this.stateToInsulation(modernizationsAndInsulation!);
        const buildingWithoutModernizations = {
            ...building,
            ...insulations
        };

        return { building: buildingWithoutModernizations };
    }

    private stateToInsulation(insulations: ModernizationsAndInsulation): Partial<BuildingPartialPatchV1Patch> {
        const payload = {
            roofInsulation: centimetersToMeters(+insulations!.roofInsulation!),
            atticInsulation: centimetersToMeters(+insulations!.atticInsulation!),
            wallsInsulation: centimetersToMeters(+insulations!.wallsInsulation!)
        };
        if (LodashService.isNil(insulations.cellarInsulation)) {
            return payload;
        }
        return {
            ...payload,
            cellarInsulation: centimetersToMeters(+insulations!.cellarInsulation!)
        };
    }

    private stateToBuildingHullModernization(modernizations: ModernizationsAndInsulation): Partial<BuildingPartialPatchV1Patch> {
        const buildingConstructionYear = +modernizations!.buildingConstructionYear!;
        const request: Partial<BuildingPartialPatchV1Patch> = {
            buildingConstructionYear,
            windowsConstructionYear: +(modernizations!.windowsConstructionYear || buildingConstructionYear),
            wallsConstructionYear: +(modernizations!.wallsRenovationAndInsulation?.wallsConstructionYear || buildingConstructionYear),
            roofConstructionYear: +(modernizations!.roofRenovationAndInsulation?.roofConstructionYear || buildingConstructionYear),
            atticConstructionYear: +(modernizations!.upperFloorRenovationAndInsulation?.atticConstructionYear || buildingConstructionYear),
            roofInsulation: centimetersToMeters(+modernizations!.roofRenovationAndInsulation!.roofInsulation),
            atticInsulation: centimetersToMeters(+modernizations!.upperFloorRenovationAndInsulation!.atticInsulation),
            wallsInsulation: centimetersToMeters(+modernizations!.wallsRenovationAndInsulation!.wallsInsulation!)
        };

        const basementRenovationAndInsulation = modernizations.basementRenovationAndInsulation;

        if (basementRenovationAndInsulation) {
            request.cellarConstructionYear = +(basementRenovationAndInsulation.cellarConstructionYear || buildingConstructionYear);
            request.cellarInsulation = centimetersToMeters(basementRenovationAndInsulation.cellarInsulation);
        }

        return request;
    }

    private stateToRoof(type: RoofType | undefined, roof: RoofDimensions | undefined): Roof {
        return {
            type: type || RoofType.Flat,
            jambWallHeight: roof?.heightOfJamb,
            slope1: roof?.inclination1 || roof?.inclination,
            slope2: roof?.inclination2,
            height1: roof?.roofHeight1,
            height2: roof?.roofHeight2,
            s1: roof?.roofLength
        };
    }

    /**
     * Builds request payload for patching project with calculated heatload and specific information from consumption calculation method gathered in heatload state.
     */
    private stateToHeatloadByConsumption(options: {
        state: AppState;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const {
            heatload: { heatGeneratorDetails, heatingEnergySource, heatingDistribution, energyConsumption, solarThermalHeating }
        } = options.state;
        // TODO: CAVP-3215 - Get rid of any and restore CurrentHeatingSystemGetById once VAPI-347 is reeady
        // const currentHeatingSystem: CurrentHeatingSystemGetById = {
        const currentHeatingSystem: any = {
            heatingSystemType: heatGeneratorDetails?.boilerType,
            energySource: heatingEnergySource?.selectedEnergySource.type,
            solarThermalHeatingType: solarThermalHeating?.type,
            installationConstructionYear: heatGeneratorDetails ? +heatGeneratorDetails.installationYear! : undefined,
            isLowTemperature: heatGeneratorDetails?.lowTemperature,
            heatGeneratorPower: heatGeneratorDetails?.nominalPower,
            exhaustGasLosses: heatGeneratorDetails?.gasLossExhaust || DEFAULT_EXHAUST_GAS_LOSSES,
            customConsumption: energyConsumption?.requiredEnergyPerYear,
            heatPumpOperatingMode: heatGeneratorDetails?.heatPumpOperatingMode
        };
        const building: Partial<BuildingPartialPatchV1Patch> = {
            warmWaterByHeatGenerator: heatGeneratorDetails?.warmWaterByHeatGenerator,
            heatingType: heatingDistribution?.distributionMethod,
            flowTemperature: heatingDistribution?.heatingFlowTemperature,
            electricityDemand: energyConsumption?.nonHeatingElectricityPerYear,
            consumptionProfileForHeating: heatingDistribution?.heatingSchedule,
            currentHeatingSystem
        };
        if (options.heatLoadCalculations) {
            building.heatLoadCalculations = options.heatLoadCalculations;
        }
        return { building };
    }

    /**
     * Builds request payload for patching project with calculated heatload and specific information from living area calculation method gathered in heatload state.
     */
    private stateToHeatloadByLivingArea(options: {
        state: AppState;
        heatLoadCalculations?: HeatLoadCalculationGetById[];
    }): UpdateProjectRequestPartialPatchV1Patch {
        const {
            heatload,
            project: { selectedProject }
        } = options.state;
        const building: FullPartial<BuildingPartialPatchV1Patch> = {
            livingArea: heatload.livingArea!.value
        };
        if (options.heatLoadCalculations) {
            building.heatLoadCalculations = options.heatLoadCalculations;
        }
        if (selectedProject!.projectType === ProjectType.NewConstruction) {
            building.energyStandard = heatload.selectedEnergyStandard?.key;
            return { building };
        }
        const buildingConstructionYear = +heatload.modernization!.buildingConstructionYear;
        const buildingWithRenovation = {
            ...building,
            buildingConstructionYear,
            windowsConstructionYear: +(heatload.modernization!.windowsModernization || buildingConstructionYear),
            wallsConstructionYear: +(heatload.modernization!.wallsModernization || buildingConstructionYear),
            roofConstructionYear: +(heatload.modernization!.roofModernization || buildingConstructionYear),
            hasVentilation: heatload.ventilation!.value
        };
        return { building: buildingWithRenovation };
    }

    private addOptionalBuildingPlacementProperty(
        request: HeatLoadByLivingAreaRequestPost,
        selectedProject: ProjectResponseGetById | undefined
    ) {
        const buildingPlacement = selectedProject?.building?.buildingPlacement as BuildingPlacement;

        if (!LodashService.isNil(buildingPlacement)) {
            request.buildingPlacement = buildingPlacement;
        }

        return request;
    }

    private addConsumptionProfileForHeatingProperty(
        request: HeatLoadByBuildingHullRequestPost,
        selectedProject: ProjectResponseGetById | undefined
    ) {
        const consumptionProfileForHeating = selectedProject?.building?.consumptionProfileForHeating as ConsumptionProfile;

        request.consumptionProfileForHeating = !LodashService.isNil(consumptionProfileForHeating)
            ? consumptionProfileForHeating
            : DEFAULT_CONSUMPTION_PROFILE;

        return request;
    }

    private addOptionalDailyLockPeriodProperty = <T extends { dailyLockPeriod?: number }>(
        request: T,
        selectedProject: ProjectResponseGetById | undefined
    ): T => {
        const heatPumpElectrictyPice = selectedProject!.energyPrices!.find(
            energyPrice => energyPrice.energySource === EnergySource.HeatPumpElectricity
        );
        const lockPeriodTariff = heatPumpElectrictyPice?.tariffs?.find(tariff => !LodashService.isNil(tariff.dailyLockPeriod));

        if (lockPeriodTariff) {
            request.dailyLockPeriod = lockPeriodTariff.dailyLockPeriod;
        }

        return request;
    };

    private roofToBuildingHullCalculation = (heatload: HeatloadState): RoofPartialPatchV1Patch => {
        const { roofDimensions, roofType } = heatload;
        const roof: RoofPartialPatchV1Patch = {
            type: roofType?.value || RoofType.Flat
        };
        if (!LodashService.isNil(roofDimensions?.heightOfJamb)) {
            roof.jambWallHeight = roofDimensions?.heightOfJamb;
        }
        const basicInclination = roofDimensions?.inclination1 || roofDimensions?.inclination;
        if (!LodashService.isNil(basicInclination)) {
            roof.slope1 = basicInclination;
        }
        if (!LodashService.isNil(roofDimensions?.inclination2)) {
            roof.slope2 = roofDimensions?.inclination2;
        }
        if (!LodashService.isNil(roofDimensions?.roofHeight1)) {
            roof.height1 = roofDimensions?.roofHeight1;
        }
        if (!LodashService.isNil(roofDimensions?.roofHeight2)) {
            roof.height2 = roofDimensions?.roofHeight2;
        }
        if (!LodashService.isNil(roofDimensions?.roofLength)) {
            roof.s1 = roofDimensions?.roofLength;
        }
        return roof;
    };

    public getSuccessMessage(options: SuccessMessageOptions): string {
        if (!options.message && !options.suffix) {
            throw Error('Provide at least one option - "message" or "suffix" or "value"');
        }
        if (options.message && options.suffix) {
            return this.translate.instant(options.message, { value: options.suffix });
        }
        if (!options.message && options.suffix) {
            return options.suffix;
        }
        return this.translate.instant(options.message as string);
    }

    public getEnergySources(generators: HeatGeneratorInformation[], searchValue: string): HeatGeneratorEnergySourceInformation[] {
        const heatGenerator = generators.find(generator => generator.heatGeneratorType === searchValue);
        if (!heatGenerator) {
            throw Error('Energy sources are not found in the provided heat generators');
        }
        return heatGenerator.energySources;
    }

    public getSelectedSource(
        sources: HeatGeneratorEnergySourceInformation[],
        searchValue?: string
    ): HeatGeneratorEnergySourceInformation | undefined {
        if (!searchValue) {
            return;
        }
        return sources.find(source => source.type === searchValue);
    }

    public getSelectedSourceFromGenerators(
        generators: HeatGeneratorInformation[],
        generatorType: HeatGeneratorType,
        energySourceType?: EnergySource
    ): HeatGeneratorEnergySourceInformation {
        const generator = generators.find(item => item.heatGeneratorType === generatorType) as HeatGeneratorInformation;
        const isManySources = generator.energySources.length > 1;
        return isManySources ? generator.energySources.find(source => source.type === energySourceType)! : generator.energySources[0];
    }

    public isHeatGeneratorAHeatPump(heatGenerator: HeatGeneratorType, energySource: EnergySource): boolean {
        return heatGenerator === HeatGeneratorType.HeatPump && energySource === EnergySource.HeatPumpElectricity;
    }

    public isHeatGeneratorAnElectricStorageHeater(heatGenerator: HeatGeneratorType, energySource: EnergySource): boolean {
        return (
            heatGenerator === HeatGeneratorType.ElectricHeater &&
            [EnergySource.HeatPumpElectricity, EnergySource.Electricity].includes(energySource)
        );
    }

    private getCalculationMethod(heatload: HeatloadState): HeatLoadCalculationMethod {
        if (!LodashService.isNil(heatload.definedValue)) {
            return HeatLoadCalculationMethod.CustomValue;
        }
        return heatload.calculationMethod!.value;
    }

    public setSpecificDataForCalculations(options: { state: AppState }) {
        const { heatload } = options.state;

        const calculationMethod = heatload.calculationMethod?.value;

        if (calculationMethod === HeatLoadCalculationMethod.Installation) {
            const { deviceSelection } = heatload;
            this.appSpecifiCDataStore.setCalculationsData({ calculationsData: { deviceSelection: deviceSelection! } });
        }
    }
}
